import React, { ReactElement } from 'react';
import {
  map, castArray, compact, isEmpty,
} from 'lodash';
import { ParsedUrlQuery } from 'querystring';
import { humanReadableFromSlug, locationNameFromQuery } from 'lib/utils/modelSlugUtils';
import {
  CREATOR_DEFAULT_DISTANCE, NETWORK_DETAILS, USER_TYPES,
} from 'lib/constants';
import { PostSort } from 'lib/types/filters';
import { defaultSort, postSortOptions } from 'lib/utils/filterUtils';
import { creatorSortOptions } from 'server/api/services/creatorSearch/constants';
import { DateTime } from 'luxon';

export const COLLECTION_KEYS = {
  minViews: 'minViews',
  maxViews: 'maxViews',
  minComments: 'minComments',
  maxComments: 'maxComments',
  minShares: 'minShares',
  maxShares: 'maxShares',
  minLikes: 'minLikes',
  maxLikes: 'maxLikes',
  users: 'users',
  role: 'role',
  event: 'events',
  creatorTags: 'creatorTags',
  tag: 'tags',
  keyword: 'keyword',
  networks: 'networks',
  fileType: 'fileType',
  minPostedAt: 'minPostedAt',
  maxPostedAt: 'maxPostedAt',
  postingAccount: 'postingAccount',
  sort: 'sort',
  importedBy: 'importedBy',
  importedAfter: 'importedAfter',
  importId: 'importId',
  username: 'username',
  importedBefore: 'importedBefore',
  importName: 'importName',
  includeUncredited: 'includeUncredited',
  creditedStatus: 'creditedStatus',
  userType: 'userType',
  collaborators: 'collaborators',
  near: 'near',
  onlyVerified: 'onlyVerified',
  page: 'page',
  permission: 'permission',
  posts: 'posts',
  byCollaborator: 'byCollaborator',
  byCollaboratorType: 'byCollaboratorType',
  byCollaboratorRole: 'byCollaboratorRole',
  savedCreators: 'savedCreators',
  list: 'list',
  lists: 'lists',
  signupStatus: 'signupStatus',
  imageSearch: 'imageSearch',
  status: 'status',
  q: 'q',
  teamId: 'teamId',
  teams: 'teams',
  project: 'project',
  projects: 'projects',
  projectTags: 'projectTags',
  region: 'region',
  excludeUserId: 'excludeUserId',
  subscriptionType: 'subscriptionType',
};

type CO = typeof creatorSortOptions;

const pluralize = require('pluralize');

pluralize.addUncountableRule('Director of Photography');
pluralize.addUncountableRule('Makeup');
pluralize.addUncountableRule('Talent');
pluralize.addUncountableRule('Stock Photography');
pluralize.addUncountableRule('First Assistant Camera');
pluralize.addUncountableRule('Second Assistant Camera');

pluralize.addIrregularRule('Digital Imaging Technician (DIT)', 'Digital Imaging Technicians (DIT)');
pluralize.addIrregularRule('Best Boy (Grip)', 'Best Boys (Grip)');
pluralize.addIrregularRule('Best Boy (Electric)', 'Best Boys (Electric)');
pluralize.addIrregularRule('Extra / Talent', 'Extras / Talent');
pluralize.addIrregularRule('Cutter / Fitter', 'Cutters / Fitters');
pluralize.addIrregularRule('Caterer / Craft Services', 'Caterers / Craft Services');
pluralize.addIrregularRule('Collective/Community', 'Collectives/Communities');
pluralize.addIrregularRule('Writer/Author', 'Writers/Authors');

// TODO: Delete this in favor of plurals in the DB
export const pluralizeRole = (role: string) => pluralize(role);

export const pluralizeUserType = (type: string) => {
  if (type === USER_TYPES.company.key) {
    return 'companies';
  }

  if (type === USER_TYPES.all.key) {
    return 'creators and companies';
  }

  return 'creators';
};

const userFilterDescription = (users: string) => castArray(users).map((u) => `@${humanReadableFromSlug(u)}`).join(' or ');
const fileTypeDescription = (fileTypes: string) => castArray(fileTypes).map((ft) => `${ft}s`.toLowerCase()).join(' or ');
const networkDescription = (networks: string[]) => networks
  .map((n) => NETWORK_DETAILS[n]?.title)
  .join(' or ');
const roleFilterDescription = (roles: string) => new Intl.ListFormat('en', { style: 'long', type: 'disjunction' })
  .format(
    castArray(roles).map((r) => humanReadableFromSlug(r).toLowerCase()),
  );
const postingAccountDescription = (accounts: string) => castArray(accounts).map((a) => `@${a}`).join(' or ');
const filterDescription = (filters: string) => castArray(filters).map((f) => humanReadableFromSlug(f)).join(' or ');
const pluralFilterDescription = (v: string) => castArray(v).map((t) => pluralize(humanReadableFromSlug(t))).join(' or ');
const truncatedFilterDescription = (filters: string, maxItems: number = 5) => {
  const items = castArray(filters);
  const displayItems = items.slice(0, maxItems);
  const remaining = items.length - maxItems;
  const displayText = displayItems.map((f) => humanReadableFromSlug(f)).join(' or ');
  return remaining > 0 ? `${displayText} and ${remaining} more` : displayText;
};

const safeLocaleString = (value: string) => parseInt(value as string, 10)?.toLocaleString();

const sWith = ' with ';
const sWithAtLeast = ' with at least ';
const sWithAtMost = ' with at most ';
const sIncluding = ' including ';
const sExcluding = ' excluding ';

const distanceFilterDescription = (query: ParsedUrlQuery) => {
  const { near, distance } = query;

  return (
    <>
      {' '}
      located near
      {' '}
      <b>
        {humanReadableFromSlug(near as string)}
      </b>
      {` (within ${distance || CREATOR_DEFAULT_DISTANCE} miles)`}
    </>
  );
};

const geoDistanceFilterDescription = (query: ParsedUrlQuery) => {
  const { geo, distance } = query;
  const location = locationNameFromQuery(humanReadableFromSlug(geo as string));

  return (
    <>
      {' '}
      located near
      {' '}
      <b>
        {location}
      </b>
      {` (within ${distance || CREATOR_DEFAULT_DISTANCE} miles)`}
    </>
  );
};

const creatorPostedAtDescription = (query: ParsedUrlQuery) => {
  const { minPostedAt, maxPostedAt } = query;

  if (!minPostedAt && !maxPostedAt) return '';

  if (minPostedAt && !maxPostedAt) {
    return (
      <>
        {' '}
        credited on posts posted after
        {' '}
        <b>{DateTime.fromISO(minPostedAt as string).toLocaleString()}</b>
      </>
    );
  }

  if (!minPostedAt && maxPostedAt) {
    return (
      <>
        {' '}
        credited on posts posted before
        {' '}
        <b>{DateTime.fromISO(maxPostedAt as string).toLocaleString()}</b>
      </>
    );
  }

  return (
    <>
      {' '}
      credited on posts posted between
      {' '}
      <b>{DateTime.fromISO(minPostedAt as string).toLocaleString()}</b>
      {' '}
      and
      {' '}
      <b>{DateTime.fromISO(maxPostedAt as string).toLocaleString()}</b>
    </>
  );
};

// TODO: the collaborator descriptions are all the same: 'worked with ____' so we should try to
// merge them
const creatorFilterDescriptions = {
  [COLLECTION_KEYS.role]: (v: string) => (
    <>
      {sWith}
      <b>{roleFilterDescription(v)}</b>
      {' '}
      credits
    </>
  ),
  [COLLECTION_KEYS.username]: (v: string) => (
    <>
      {' '}
      that match the username
      {' '}
      <b>
        @
        {v}
      </b>
    </>
  ),
  [COLLECTION_KEYS.sort]: (v: keyof CO) => (
    <>
      {' '}
      sorted by
      {' '}
      <b>
        {creatorSortOptions[v]?.toLocaleLowerCase()
          || v
          || creatorSortOptions.mostRecentCredit?.toLocaleLowerCase()}
      </b>
    </>
  ),
  [COLLECTION_KEYS.tag]: (v: string) => (
    <>
      {' '}
      whose posts include the tag
      {' '}
      &apos;
      <b>{filterDescription(v)}</b>
      &apos;
    </>
  ),
  [COLLECTION_KEYS.creatorTags]: (v: string) => `tagged '${humanReadableFromSlug(v)}'`,
  [COLLECTION_KEYS.event]: (v: string) => (
    <>
      {' '}
      whose posts include the event
      {' '}
      &apos;
      <b>{filterDescription(v)}</b>
      &apos;
    </>
  ),
  [COLLECTION_KEYS.keyword]: (v: string) => (
    <>
      {' '}
      whose posts include the keyword(s)
      {' '}
      <b>{filterDescription(v)}</b>
    </>
  ),
  [COLLECTION_KEYS.q]: (v: string) => (
    <>
      {' '}
      that match the query
      {' '}
      <b>{filterDescription(v)}</b>
    </>
  ),

  [COLLECTION_KEYS.byCollaborator]: (v: string) => (
    <>
      {' '}
      who have worked with
      {' '}
      <b>{userFilterDescription(v)}</b>
    </>
  ),
  [COLLECTION_KEYS.byCollaboratorType]: (v: string) => (
    <>
      {' '}
      who have worked with
      {' '}
      <b>
        {filterDescription(v)?.toLowerCase()}
        {' '}
        brands and creators
      </b>
    </>
  ),
  [COLLECTION_KEYS.byCollaboratorRole]: (v: string) => (
    <>
      {' '}
      who have worked with
      {' '}
      <b>{pluralFilterDescription(v)}</b>
    </>
  ),
  [COLLECTION_KEYS.collaborators]: (v: string) => `who have worked with ${userFilterDescription(v)}`,

  [COLLECTION_KEYS.users]: (v: string) => (
    <>
      {sIncluding}
      <b>{userFilterDescription(v)}</b>
    </>
  ),
  [COLLECTION_KEYS.savedCreators]: () => (
    <>
      {' '}
      you have saved
      {' '}
    </>
  ),
  [COLLECTION_KEYS.list]: (v: string) => (
    <>
      {' '}
      saved to
      {' '}
      &apos;
      <b>{filterDescription(v)}</b>
      &apos;
      {' '}
      list
    </>
  ),
  [COLLECTION_KEYS.teamId]: (v: string) => (
    <>
      {' '}
      on the
      {' '}
      <b>{filterDescription(v)}</b>
      {' '}
      team
    </>
  ),
  [COLLECTION_KEYS.region]: (v: string) => (
    <>
      {' '}
      in
      {' '}
      <b>{humanReadableFromSlug(v)}</b>
    </>
  ),
};

const creatorFollowersDescription = (query: ParsedUrlQuery) => {
  const { minFollowers, maxFollowers } = query;

  if (!maxFollowers && !minFollowers) {
    return '';
  }

  if (!minFollowers && (parseInt(maxFollowers as string, 10) || 0) > 0) {
    return ` with up to ${safeLocaleString(maxFollowers as string)} followers`;
  }

  if (!maxFollowers && (parseInt(minFollowers as string, 10) || 0) > 0) {
    return ` with at least ${safeLocaleString(minFollowers as string)} followers`;
  }

  return ` with ${safeLocaleString(minFollowers as string)} - ${safeLocaleString(maxFollowers as string)} followers`;
};

const postFilterDescriptions = {
  [COLLECTION_KEYS.users]: (v: string) => (
    <>
      {sWith}
      <b>{userFilterDescription(v)}</b>
    </>
  ),
  [COLLECTION_KEYS.role]: (v: string) => (
    <>
      {sWith}
      <b>{roleFilterDescription(v)}</b>
      {' '}
      credits
    </>
  ),
  [COLLECTION_KEYS.event]: (v: string) => (
    <>
      {' '}
      from
      {' '}
      &apos;
      <b>{filterDescription(v)}</b>
      &apos;
    </>
  ),
  [COLLECTION_KEYS.tag]: (v: string) => (
    <>
      {' '}
      tagged
      {' '}
      <b>
        &apos;
        <b>{filterDescription(v)}</b>
        &apos;
      </b>
    </>
  ),
  [COLLECTION_KEYS.keyword]: (v: string) => (
    <>
      {' '}
      matching the keyword(s)
      {' '}
      <b>{filterDescription(v)}</b>
    </>
  ),
  [COLLECTION_KEYS.networks]: (v: string) => {
    const validNetworks = castArray(v).filter((nw) => !!NETWORK_DETAILS[nw]);

    if (validNetworks.length === 0) return '';

    return (
      <>
        {' '}
        on
        {' '}
        <b>{networkDescription(validNetworks)}</b>
      </>
    );
  },
  [COLLECTION_KEYS.fileType]: (v: string) => (
    <>
      {' '}
      that are
      {' '}
      <b>{fileTypeDescription(v)}</b>
    </>
  ),
  [COLLECTION_KEYS.postingAccount]: (v: string) => (
    <>
      {' '}
      posted by
      {' '}
      <b>
        {postingAccountDescription(v)}
      </b>
    </>
  ),
  [COLLECTION_KEYS.sort]: (v: PostSort) => (
    <>
      {' '}
      sorted by
      {' '}
      <b>
        {postSortOptions[v]?.toLocaleLowerCase()
          || postSortOptions[defaultSort].toLocaleLowerCase()}
      </b>
    </>
  ),
  [COLLECTION_KEYS.importedAfter]: (v: string) => `imported after ${v}`,
  [COLLECTION_KEYS.importedBefore]: (v: string) => `imported before ${v}`,
  [COLLECTION_KEYS.near]: (v: string) => (
    <>
      {' '}
      located near
      {' '}
      <b>
        {humanReadableFromSlug(v)}
      </b>
    </>
  ),
  [COLLECTION_KEYS.region]: (v: string) => (
    <>
      {' '}
      in
      {' '}
      <b>
        {humanReadableFromSlug(v)}
      </b>
    </>
  ),
  [COLLECTION_KEYS.minPostedAt]: (v: string) => (
    <>
      {' '}
      posted after
      {' '}
      <b>{DateTime.fromISO(v).toLocaleString()}</b>
    </>
  ),
  [COLLECTION_KEYS.maxPostedAt]: (v: string) => (
    <>
      {' '}
      posted before
      {' '}
      <b>{DateTime.fromISO(v).toLocaleString()}</b>
    </>
  ),
  [COLLECTION_KEYS.minViews]: (v: string) => (
    <>
      {sWithAtLeast}
      <b>{safeLocaleString(v)}</b>
      {' '}
      views
    </>
  ),
  [COLLECTION_KEYS.minComments]: (v: string) => (
    <>
      {sWithAtLeast}
      <b>{safeLocaleString(v)}</b>
      {' '}
      comments
    </>
  ),
  [COLLECTION_KEYS.minShares]: (v: string) => (
    <>
      {sWithAtLeast}
      <b>{safeLocaleString(v)}</b>
      {' '}
      shares
    </>
  ),
  [COLLECTION_KEYS.minLikes]: (v: string) => (
    <>
      {sWithAtLeast}
      <b>{safeLocaleString(v)}</b>
      {' '}
      likes
    </>
  ),
  [COLLECTION_KEYS.maxViews]: (v: string) => (
    <>
      {sWithAtMost}
      <b>{safeLocaleString(v)}</b>
      {' '}
      views
    </>
  ),
  [COLLECTION_KEYS.maxComments]: (v: string) => (
    <>
      {sWithAtMost}
      <b>{safeLocaleString(v)}</b>
      {' '}
      comments
    </>
  ),
  [COLLECTION_KEYS.maxShares]: (v: string) => (
    <>
      {sWithAtMost}
      <b>{safeLocaleString(v)}</b>
      {' '}
      shares
    </>
  ),
  [COLLECTION_KEYS.maxLikes]: (v: string) => (
    <>
      {sWithAtMost}
      <b>{safeLocaleString(v)}</b>
      {' '}
      likes
    </>
  ),
  [COLLECTION_KEYS.excludeUserId]: (v: string) => (
    <>
      {sExcluding}
      <b>{userFilterDescription(v)}</b>
    </>
  ),

  onlyFromSavedCreators: () => (
    <>
      {' '}
      by
      {' '}
      <b>saved creators</b>
    </>
  ),
  includeUncredited: () => (
    <>
      {' '}
      including
      {' '}
      <b>uncredited</b>
    </>
  ),
  creditedStatus: (v: string) => {
    if (v === 'suggested') {
      return (
        <>
          {' '}
          with
          {' '}
          <b>suggested credits</b>
          {' '}
          for you
          {' '}
        </>
      );
    }
    if (v === 'credited') {
      return (
        <>
          <b>
            {' '}
            with credits
            {' '}
          </b>
        </>
      );
    }
    if (v === 'uncredited') {
      return (
        <>
          <b>
            {' '}
            without credits
            {' '}
          </b>
        </>
      );
    }
    if (v === 'single') {
      return (
        <>
          {' '}
          with
          {' '}
          <b>
            exactly one credit
          </b>
          {' '}
        </>
      );
    }

    return (
      <>
        {' '}
        <b>
          with or without credits
        </b>
        {' '}
      </>
    );
  },

  [COLLECTION_KEYS.list]: (v: string) => (
    <>
      {' '}
      with creators or companies saved to
      {' '}
      &apos;
      <b>{filterDescription(v)}</b>
      &apos;
      {' '}
      list
    </>
  ),
  [COLLECTION_KEYS.imageSearch]: (v: string) => (
    <>
      {' '}
      with images that contain
      {' '}
      <b>
        &quot;
        {v}
        &quot;
      </b>
    </>
  ),
  [COLLECTION_KEYS.importId]: (v: string) => (
    <>
      {' '}
      from
      {' '}
      <b>
        {filterDescription(v)}
      </b>
      {' '}
      bulk import
    </>
  ),
  [COLLECTION_KEYS.project]: (v: string) => (
    <>
      {' '}
      on
      {' '}
      <b>{filterDescription(v)}</b>
      {' '}
      project
    </>
  ),
  [COLLECTION_KEYS.projects]: (v: string) => (
    <>
      {' '}
      on
      {' '}
      <b>{filterDescription(v)}</b>
      {' '}
      project
    </>
  ),
};

const analyticsFilterDescriptions = {
  ...postFilterDescriptions,
  [COLLECTION_KEYS.list]: (v: string) => (
    <>
      {' '}
      saved to
      {' '}
      <b>{truncatedFilterDescription(v)}</b>
      {' '}
      list
    </>
  ),
  [COLLECTION_KEYS.projectTags]: (v: string) => (
    <>
      {' '}
      with projects tagged
      {' '}
      <b>{truncatedFilterDescription(v)}</b>
    </>
  ),
  [COLLECTION_KEYS.teams]: (v: string) => (
    <>
      {' '}
      on
      {' '}
      <b>{truncatedFilterDescription(v)}</b>
      {' '}
      team accounts
    </>
  ),
  [COLLECTION_KEYS.lists]: (v: string) => (
    <>
      {' '}
      saved to
      {' '}
      <b>{truncatedFilterDescription(v)}</b>
      {' '}
      list
    </>
  ),
  [COLLECTION_KEYS.projects]: (v: string) => (
    <>
      {' '}
      on
      {' '}
      <b>{truncatedFilterDescription(v)}</b>
      {' '}
      {castArray(v).length === 1 ? 'project' : 'projects'}
    </>
  ),
};

/*
From https://github.com/kevinzwhuang/react-to-string/blob/master/src/react-to-string.js
converted to typescript
*/
export const reactToString = (element: string | ReactElement): string => {
  if (!element) {
    return '';
  }

  if (typeof element === 'string') {
    return element;
  }

  if (typeof element === 'number') {
    return String(element);
  }

  if (Array.isArray(element)) {
    return element.map((subElement) => reactToString(subElement)).join('');
  }

  if (element.props && element.props.children) {
    return reactToString(element.props.children);
  }

  if (element.props && !element.props.children) {
    return '';
  }

  return '';
};

/**
 * A special case where there is only a location filter and optional
 * roles filter. It will say something like "New York creators" or
 * "Chicago photographers". If it doesn't meet those criteria,
 * it will return undefined.
 */
function locationRoleFilterDescription(query: ParsedUrlQuery) {
  if (
    (Object.keys(query).length === 1 && query.geo)
    || (Object.keys(query).length === 2 && query.geo && query.role)
  ) {
    const location = locationNameFromQuery(humanReadableFromSlug(query.geo as string));
    const roles = query.role
      ? new Intl.ListFormat('en', { style: 'long', type: 'disjunction' })
        .format(castArray(query.role)
          .map((r) => pluralizeRole(humanReadableFromSlug(r))))
      : 'creators';

    return (
      <span className="font-semibold">
        {location}
        {' '}
        {roles}
      </span>
    );
  }

  return undefined;
}

export const creatorFilterDescription = (
  query: ParsedUrlQuery,
  baseDescription: string,
  richText: boolean = false,
) => {
  if (isEmpty(query)) {
    return baseDescription;
  }

  const locationRoleDescription = locationRoleFilterDescription(query);
  if (locationRoleDescription) {
    if (!richText) {
      return reactToString(locationRoleDescription);
    }
    return (
      <>
        {locationRoleDescription}
      </>
    );
  }

  const descriptions = compact(map(
    query, (v: any, k) => creatorFilterDescriptions[k]
      && <span key={k}>{creatorFilterDescriptions[k](v)}</span>,
  ));

  if (query.near) {
    descriptions.push(<span key="near">{distanceFilterDescription(query)}</span>);
  }

  if (query.geo) {
    descriptions.push(<span key="geo">{geoDistanceFilterDescription(query)}</span>);
  }

  if (query.minPostedAt || query.maxPostedAt) {
    descriptions.push(<span key="postedAt">{creatorPostedAtDescription(query)}</span>);
  }

  if (!query.sort) {
    const hasMetadataFilters = query.brands || query.people || query.companies
      || query.industries || query.products;
    if (query.list) {
      descriptions.push(<span key="sort">{creatorFilterDescriptions[COLLECTION_KEYS.sort]('submissionDate')}</span>);
    } else if (hasMetadataFilters) {
      descriptions.push(<span key="sort">{creatorFilterDescriptions[COLLECTION_KEYS.sort]('relevance')}</span>);
    } else {
      descriptions.push(<span key="sort">{creatorFilterDescriptions[COLLECTION_KEYS.sort]('mostRecentCredit')}</span>);
    }
  }

  if (query.minFollowers || query.maxFollowers) {
    descriptions.push(<span key="followers">{creatorFollowersDescription(query)}</span>);
  }

  if (!richText) {
    const elToString = descriptions.map((e) => reactToString(e)).join('');
    return `${baseDescription}${elToString}`;
  }

  return (
    <>
      {baseDescription}
      {descriptions}
    </>
  );
};

export const postFilterDescription = (
  query: ParsedUrlQuery,
  baseDescription: string,
  richText: boolean = false,
) => {
  if (isEmpty(query)) {
    return '';
  }

  const descriptions = compact(map(
    query, (v: any, k) => postFilterDescriptions[k]
      && <span key={k}>{postFilterDescriptions[k](v)}</span>,
  ));

  if (!richText) {
    const elToString = descriptions.map((e) => reactToString(e)).join('');
    return `${baseDescription}${elToString}`;
  }

  return (
    <>
      {baseDescription}
      {descriptions}
    </>
  );
};

export const analyticsFilterDescription = (
  query: ParsedUrlQuery,
  baseDescription: string,
  richText: boolean = false,
  singleLine: boolean = false,
) => {
  if (isEmpty(query)) {
    return '';
  }

  const userTypeText = 'Posts';

  const descriptions = compact(map(
    query, (v: any, k) => analyticsFilterDescriptions[k]
      && <span key={k}>{analyticsFilterDescriptions[k](v)}</span>,
  ));
  const fullDescription = `${baseDescription} ${userTypeText}`;

  if (!richText) {
    const elToString = descriptions.map((e) => reactToString(e)).join('');
    return `${fullDescription}${elToString}`;
  }

  if (singleLine) {
    return (
      <span>
        {fullDescription}
        {descriptions.slice(1, 3).map((desc) => (
          <React.Fragment key={desc.key || Math.random()}>
            {desc}
          </React.Fragment>
        ))}
        ...
      </span>
    );
  }

  return (
    <>
      {fullDescription}
      {descriptions}
    </>
  );
};
